/**
 * The "Edit" form for a skill: title, name (slug), description, instructions
 * (markdown), the library entries it references, the firm's other skills it
 * follows, and a one-line note for the history. Everything the builder
 * writes can also be written by hand here.
 */
import { Plus, X } from "lucide-react";
import { useMemo, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import type {
  FirmLibraryEntry,
  FirmSkill,
  SkillEdits,
  SkillReferenceRole
} from "@/features/firmKnowledge/types";

type Ref = { entry_id: string; role: SkillReferenceRole; note: string };

type Props = {
  skill: FirmSkill;
  /** Active library entries a reference may point at. */
  library: FirmLibraryEntry[];
  /** The firm's other skills this one may follow (self is filtered out). */
  otherSkills?: Pick<FirmSkill, "id" | "title" | "slug" | "status">[];
  saving: boolean;
  onSave: (edits: SkillEdits) => Promise<void>;
  onCancel: () => void;
};

const ROLES: { value: SkillReferenceRole; label: string; hint: string }[] = [
  { value: "exemplar", label: "Exemplar", hint: "imitate its prose and shape" },
  { value: "shell", label: "Shell", hint: "fill it in place" },
  { value: "guidance", label: "Guidance", hint: "read for house rules" }
];

export const SkillEditor: FC<Props> = ({
  skill,
  library,
  otherSkills = [],
  saving,
  onSave,
  onCancel
}) => {
  const [title, setTitle] = useState(skill.title);
  const [uses, setUses] = useState<string[]>(
    skill.uses_skills.map((u) => u.skill_id)
  );
  const [addingSkill, setAddingSkill] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const candidates = otherSkills.filter(
    (s) => s.id !== skill.id && !uses.includes(s.id)
  );
  const skillName = (id: string) =>
    otherSkills.find((s) => s.id === id)?.title ??
    skill.uses_skills.find((u) => u.skill_id === id)?.title ??
    id;
  const [slug, setSlug] = useState(skill.slug);
  const [description, setDescription] = useState(skill.description);
  const [instructions, setInstructions] = useState(skill.instructions);
  const [refs, setRefs] = useState<Ref[]>(
    skill.references.map((r) => ({
      entry_id: r.entry_id,
      role: r.role,
      note: r.note
    }))
  );
  const [adding, setAdding] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const byId = useMemo(() => new Map(library.map((e) => [e.id, e])), [library]);
  const available = library.filter(
    (e) => !refs.some((r) => r.entry_id === e.id)
  );

  const submit = async () => {
    setError(null);
    const edits: SkillEdits = {};
    if (title !== skill.title) edits.title = title;
    if (slug !== skill.slug) edits.slug = slug;
    if (description !== skill.description) edits.description = description;
    if (instructions !== skill.instructions) edits.instructions = instructions;
    const before = JSON.stringify(
      skill.references.map((r) => [r.entry_id, r.role, r.note])
    );
    const after = JSON.stringify(refs.map((r) => [r.entry_id, r.role, r.note]));
    if (before !== after) edits.references = refs;
    const usesBefore = skill.uses_skills.map((u) => u.skill_id).join(",");
    if (uses.join(",") !== usesBefore) edits.uses_skills = uses;
    if (!Object.keys(edits).length) {
      onCancel();
      return;
    }
    if (changeNote.trim()) edits.change_note = changeNote.trim();
    try {
      await onSave(edits);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not save the automation."
      );
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      data-testid="skill-editor"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="skill-title">Title</Label>
          <Input
            id="skill-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="skill-slug">Name</Label>
          <Input
            id="skill-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            maxLength={64}
            className="font-mono text-sm"
            required
          />
          <p className="text-sm text-fg-3">
            Lower-case letters, digits and hyphens; how the agent refers to it.
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="skill-description">Description</Label>
        <Textarea
          id="skill-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={1024}
          placeholder="What it does and when the agent should use it."
        />
        <p className="text-sm text-fg-3">
          The agent picks automations by reading this. Name the requests that
          should trigger it.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="skill-instructions">Instructions</Label>
        <Textarea
          id="skill-instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={16}
          className="font-mono text-sm leading-5"
          placeholder="Plain English: purpose, when to use it, what you need first, numbered steps, the result, what never to do."
        />
        <p className="text-sm text-fg-3">
          Write it the way you would brief a new paralegal. Do not name the
          assistant's tools or any code.
        </p>
      </div>

      <div className="space-y-2">
        <Label>References</Label>
        {refs.length === 0 ? (
          <p className="text-sm text-fg-3">
            No library entries attached. Attach the exemplars or shells the
            steps lean on.
          </p>
        ) : null}
        <ul className="flex flex-col gap-2">
          {refs.map((r, i) => {
            const entry = byId.get(r.entry_id);
            return (
              <li
                key={r.entry_id}
                className="flex flex-wrap items-center gap-2 rounded-lg ring-1 ring-line-2 px-2 py-1.5"
                data-testid="skill-editor-reference"
              >
                <span className="min-w-0 flex-1 truncate text-sm">
                  {entry?.name ??
                    skill.references.find((x) => x.entry_id === r.entry_id)
                      ?.name ??
                    r.entry_id}
                </span>
                <Select
                  value={r.role}
                  onValueChange={(role) =>
                    setRefs((cur) =>
                      cur.map((x, j) =>
                        j === i ? { ...x, role: role as SkillReferenceRole } : x
                      )
                    )
                  }
                >
                  <SelectTrigger size="sm" className="w-32 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                        <span className="ml-1 text-sm text-fg-3">
                          {role.hint}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={r.note}
                  onChange={(e) =>
                    setRefs((cur) =>
                      cur.map((x, j) =>
                        j === i ? { ...x, note: e.target.value } : x
                      )
                    )
                  }
                  placeholder="Why this one"
                  className="h-8 w-48 text-sm"
                  maxLength={500}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Remove reference"
                  onClick={() =>
                    setRefs((cur) => cur.filter((_, j) => j !== i))
                  }
                >
                  <X className="size-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
        {available.length ? (
          <div className="flex items-center gap-2">
            <Select value={adding} onValueChange={setAdding}>
              <SelectTrigger size="sm" className="w-72 text-sm">
                <SelectValue placeholder="Attach a library entry…" />
              </SelectTrigger>
              <SelectContent>
                {available.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!adding}
              onClick={() => {
                if (!adding) return;
                setRefs((cur) => [
                  ...cur,
                  { entry_id: adding, role: "exemplar", note: "" }
                ]);
                setAdding("");
              }}
              data-testid="skill-editor-add-reference"
            >
              <Plus className="size-3.5" /> Attach
            </Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-2" data-testid="skill-editor-uses">
        <Label>Follows These Automations</Label>
        {uses.length === 0 ? (
          <p className="text-sm text-fg-3">
            None. A long automation can hand a step to another of the firm's
            automations.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {uses.map((id) => (
              <li
                key={id}
                className="inline-flex items-center gap-1 rounded-md ring-1 ring-line-2 px-2 py-1 text-sm"
                data-testid="skill-editor-use"
              >
                {skillName(id)}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Stop following ${skillName(id)}`}
                  onClick={() => setUses((cur) => cur.filter((x) => x !== id))}
                >
                  <X className="size-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {candidates.length ? (
          <div className="flex items-center gap-2">
            <Select value={addingSkill} onValueChange={setAddingSkill}>
              <SelectTrigger size="sm" className="w-72 text-sm">
                <SelectValue placeholder="Follow another automation…" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.title || s.slug}
                    {s.status !== "active" ? (
                      <span className="ml-1 text-sm text-fg-3">
                        not enabled
                      </span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!addingSkill}
              onClick={() => {
                if (!addingSkill) return;
                setUses((cur) => [...cur, addingSkill]);
                setAddingSkill("");
              }}
              data-testid="skill-editor-add-use"
            >
              <Plus className="size-3.5" /> Follow
            </Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="skill-change-note">
          What Changed (for the History)
        </Label>
        <Input
          id="skill-change-note"
          value={changeNote}
          onChange={(e) => setChangeNote(e.target.value)}
          maxLength={300}
          placeholder="Tightened the steps for the cover letter"
          data-testid="skill-change-note"
        />
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={saving}
          data-testid="skill-editor-save"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
};
